import React from 'react';

import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {type StepMeta} from '../types';
import {type CompareModeRunPair} from '../utils/compareEntryConfig';
import {MediaBrowserFullscreenModal} from './MediaBrowserFullscreenModal';
import {
  MediaBrowserModalProvider,
  useMediaBrowserModalContext,
} from './MediaBrowserModalContext';
import {type MediaBrowserModalProps} from './types';

// Re-export for convenience
export type {ModalData} from './types';

type MediaBrowserModalContainerProps = {
  children: React.ReactNode;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  sliderConfig: {
    snapToExistingStep: boolean;
    stepStrideLength: number;
  };
  stepMeta?: StepMeta;
  onModalClose?: (finalStep: number, finalIndex: number) => void;
  onCompareRuns?: (runPair: CompareModeRunPair, mediaKey?: string) => void;
  /*
   * NOTE: this prop allows unit tests to mock the underlying modal component.
   * It should only be used for testing purposes, and not provided directly outside of tests.
   */
  modalComponent?: React.ComponentType<MediaBrowserModalProps>;
};

// Separate component needed because useMediaBrowserModalContext() must be called
// inside the MediaBrowserModalProvider, but MediaBrowserModalContainer creates the provider.
// This prevents the "must be used within a MediaBrowserModalProvider" error.
const ModalContent: React.FC<{
  modalComponent?: React.ComponentType<MediaBrowserModalProps>;
}> = ({modalComponent}) => {
  const ModalComponent = modalComponent ?? MediaBrowserFullscreenModal;
  const {isOpen, modalData, closeModal, updateModalData} =
    useMediaBrowserModalContext();

  if (!isOpen || !ModalComponent) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      modalData={modalData}
      onClose={closeModal}
      updateModalData={updateModalData}
    />
  );
};

export const MediaBrowserModalContainer: React.FC<
  MediaBrowserModalContainerProps
> = ({
  children,
  modalComponent,
  runsWithMedia,
  sliderConfig,
  stepMeta,
  onModalClose,
  onCompareRuns,
}) => {
  return (
    <MediaBrowserModalProvider
      runsWithMedia={runsWithMedia}
      sliderConfig={sliderConfig}
      stepMeta={stepMeta}
      onModalClose={onModalClose}
      onCompareRuns={onCompareRuns}>
      {children}
      <ModalContent modalComponent={modalComponent} />
    </MediaBrowserModalProvider>
  );
};
